import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { useAuth } from '@/contexts/AuthContext';
import { projectsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  FileText, Plus, MoreVertical, Settings, LogOut, User,
  Clock, TrendingUp, AlertTriangle, CheckCircle, Archive,
  Trash2, Eye, BarChart3, Lightbulb, Shield, Upload
} from 'lucide-react';
import { STATUS_COLORS, PRICING_TIERS } from '@/lib/constants';
import { formatDate, formatDateShort } from '@/lib/dateUtils';
import Footer from '@/components/Footer';
import ProjectImportModal from '@/components/ProjectImportModal';

const DashboardPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteProject, setDeleteProject] = useState(null);
  const [closeProject, setCloseProject] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const response = await projectsAPI.getAll();
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to load projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProject = async () => {
    if (!deleteProject) return;
    try {
      await projectsAPI.delete(deleteProject.id);
      setProjects(projects.filter(p => p.id !== deleteProject.id));
    } catch (error) {
      console.error('Failed to delete project:', error);
    } finally {
      setDeleteProject(null);
    }
  };

  const handleCloseProject = async () => {
    if (!closeProject) return;
    try {
      await projectsAPI.close(closeProject.id);
      setProjects(projects.map(p => 
        p.id === closeProject.id ? { ...p, status: 'closed' } : p
      ));
    } catch (error) {
      console.error('Failed to close project:', error);
    } finally {
      setCloseProject(null);
    }
  };

  const activeProjects = projects.filter(p => p.status === 'active');
  const closedProjects = projects.filter(p => p.status === 'closed');
  const tierLimit = PRICING_TIERS[user?.tier || 'trial'].projectLimit;
  const canCreateProject = activeProjects.length < tierLimit;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="border-b bg-white sticky top-0 z-50">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2">
            <FileText className="h-7 w-7 text-blue-600" />
            <span className="text-lg font-bold text-slate-900">ProjectStatusReport</span>
          </Link>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="gap-2" data-testid="user-menu-btn">
                <User className="h-4 w-4" />
                <span className="hidden md:inline">{user?.name}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => navigate('/ideas')}>
                <Lightbulb className="mr-2 h-4 w-4" />
                Feature Ideas
              </DropdownMenuItem>
              {user?.is_admin && (
                <DropdownMenuItem onClick={() => navigate('/admin')}>
                  <Shield className="mr-2 h-4 w-4" />
                  Admin Dashboard
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => navigate('/settings')}>
                <Settings className="mr-2 h-4 w-4" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuItem onClick={logout}>
                <LogOut className="mr-2 h-4 w-4" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-900">Welcome, {user?.name?.split(' ')[0]}!</h1>
          <p className="text-slate-600">
            {user?.tier === 'trial' && 'You are on the free trial.'}
            {(user?.tier === 'professional' || user?.tier === 'business') && 'Professional plan'}
            {' '}
            <span className="text-sm">({activeProjects.length}/{tierLimit} active projects)</span>
          </p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard
            icon={<BarChart3 className="h-5 w-5" />}
            label="Active Projects"
            value={activeProjects.length}
          />
          <StatCard
            icon={<CheckCircle className="h-5 w-5 text-green-600" />}
            label="On Track"
            value={activeProjects.filter(p => p.current_status === 'on_track').length}
          />
          <StatCard
            icon={<AlertTriangle className="h-5 w-5 text-yellow-600" />}
            label="At Risk"
            value={activeProjects.filter(p => p.current_status === 'at_risk').length}
          />
          <StatCard
            icon={<Archive className="h-5 w-5 text-slate-500" />}
            label="Closed"
            value={closedProjects.length}
          />
        </div>

        {/* Create Project Buttons */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold">Your Projects</h2>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setShowImportModal(true)}
              disabled={!canCreateProject}
              className="gap-2"
              data-testid="import-project-btn"
            >
              <Upload className="h-4 w-4" />
              Import
            </Button>
            <Button
              onClick={() => navigate('/projects/new')}
              disabled={!canCreateProject}
              className="gap-2"
              data-testid="create-project-btn"
            >
              <Plus className="h-4 w-4" />
              New Project
            </Button>
          </div>
        </div>

        {!canCreateProject && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              You&apos;ve reached your project limit. 
              <Link to="/settings" className="font-medium underline ml-1">Upgrade your plan</Link>
              {' '}or close an existing project to create a new one.
            </p>
          </div>
        )}

        {/* Projects List */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map(i => (
              <Card key={i}>
                <CardHeader>
                  <Skeleton className="h-6 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-4 w-full mb-2" />
                  <Skeleton className="h-4 w-2/3" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : projects.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No projects yet</h3>
              <p className="text-slate-600 mb-4">Create your first project to get started</p>
              <Button onClick={() => navigate('/projects/new')} className="gap-2">
                <Plus className="h-4 w-4" />
                Create Project
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Active Projects */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              {activeProjects.map(project => (
                <ProjectCard
                  key={project.id}
                  project={project}
                  onView={() => navigate(`/projects/${project.id}`)}
                  onClose={() => setCloseProject(project)}
                  onDelete={() => setDeleteProject(project)}
                />
              ))}
            </div>

            {/* Closed Projects */}
            {closedProjects.length > 0 && (
              <>
                <h3 className="text-md font-semibold text-slate-500 mb-4">Closed Projects</h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 opacity-75">
                  {closedProjects.map(project => (
                    <ProjectCard
                      key={project.id}
                      project={project}
                      onView={() => navigate(`/projects/${project.id}`)}
                      onDelete={() => setDeleteProject(project)}
                      closed
                    />
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteProject} onOpenChange={() => setDeleteProject(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Project</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{deleteProject?.name}&quot;? This will permanently delete
              the project and all its reports. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteProject} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Close Confirmation Dialog */}
      <AlertDialog open={!!closeProject} onOpenChange={() => setCloseProject(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Close Project</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to close &quot;{closeProject?.name}&quot;? Closed projects can still be
              viewed but cannot be edited or have new reports added. This action is permanent.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleCloseProject}>Close Project</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Import Modal */}
      <ProjectImportModal 
        open={showImportModal} 
        onOpenChange={setShowImportModal}
      />

      <Footer />
    </div>
  );
};

const StatCard = ({ icon, label, value }) => (
  <Card>
    <CardContent className="pt-4">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-slate-100 rounded-lg">{icon}</div>
        <div>
          <p className="text-2xl font-bold">{value}</p>
          <p className="text-sm text-slate-600">{label}</p>
        </div>
      </div>
    </CardContent>
  </Card>
);

const ProjectCard = ({ project, onView, onClose, onDelete, closed }) => {
  const statusColors = STATUS_COLORS[project.current_status] || STATUS_COLORS.on_track;

  return (
    <Card className={`hover:shadow-md transition-shadow ${closed ? 'bg-slate-50' : ''}`} data-testid={`project-card-${project.id}`}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-lg flex items-center gap-2">
              {project.name}
              {closed && <Badge variant="secondary">Closed</Badge>}
            </CardTitle>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline" className="text-xs">
                {project.methodology === 'predictive' ? 'Predictive' : 'Agile'}
              </Badge>
              {!closed && (
                <Badge className={`${statusColors.bg} ${statusColors.text} border-0 text-xs`}>
                  {project.current_status === 'on_track' && 'On Track'}
                  {project.current_status === 'at_risk' && 'At Risk'}
                  {project.current_status === 'off_track' && 'Off Track'}
                </Badge>
              )}
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onView}>
                <Eye className="mr-2 h-4 w-4" />
                View
              </DropdownMenuItem>
              {!closed && onClose && (
                <DropdownMenuItem onClick={onClose}>
                  <Archive className="mr-2 h-4 w-4" />
                  Close Out
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={onDelete} className="text-red-600">
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-slate-600 mb-3">PM: {project.pm_name}</p>
        <div className="flex items-center gap-4 text-xs text-slate-500">
          <div className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {project.last_report_date 
              ? `Last report: ${formatDateShort(project.last_report_date)}`
              : 'No reports yet'
            }
          </div>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          className="w-full mt-4" 
          onClick={onView}
          data-testid={`view-project-btn-${project.id}`}
        >
          {closed ? 'View Reports' : 'Open Project'}
        </Button>
      </CardContent>
    </Card>
  );
};

export default DashboardPage;
